import { useMemo, useState } from 'react';

import PremiumGate from '@/components/premium/PremiumGate.tsx';
import { organizerSidebar } from '@/config/sidebarConfig.ts';
import { fetchAllCompetitionsForOrg } from '@/lib/api/competitionsApi';
import { fetchAllPaymentsForOrg } from '@/lib/api/paymentsApi';
import { type ExportColumn, type ExportFormat, datedFilename, exportData } from '@/lib/export/export';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import {
  COMPETITION_TYPE_LABELS as TYPE_LABELS,
  formatDateAbbreviatedFr,
  formatEur,
  isOrgaForfaitActive,
} from '@shared';
import { CompetitionStatus, CompetitionType, PaymentStatus } from '@shared/enums/domain.enum';
import type { Competition } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Download, Euro, Percent, TrendingUp, Trophy, Users2 } from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

// ─── Period config ─────────────────────────────────────────────────
type Period = 3 | 6 | 12 | 0;

const PERIOD_LABELS: Record<Period, string> = {
  3: '3 mois',
  6: '6 mois',
  12: '12 mois',
  0: 'Tout',
};

// ─── Competition type display ──────────────────────────────────────
const TYPE_COLORS: Record<CompetitionType, string> = {
  [CompetitionType.SINGLE]: 'bg-blue-500',
  [CompetitionType.DOUBLE]: 'bg-indigo-500',
  [CompetitionType.TRIPLE]: 'bg-purple-500',
};

// ─── Helpers ─────────────────────────────────────────────────────────
function monthKey(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  return dateStr.slice(0, 7); // "2025-10"
}

function formatCompetitionDate(dateStr?: string | null): string {
  return dateStr ? formatDateAbbreviatedFr(dateStr) : '—';
}

function lastNMonths(n: number): { key: string; label: string }[] {
  const result: { key: string; label: string }[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('fr-FR', { month: 'short' });
    result.push({ key, label });
  }
  return result;
}

function cutoffDate(period: Period): Date | null {
  if (period === 0) return null;
  const d = new Date();
  d.setMonth(d.getMonth() - period);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

const ACTIVE_STATUSES = [
  CompetitionStatus.REGISTRATION_OPEN,
  CompetitionStatus.REGISTRATION_CLOSED,
  CompetitionStatus.IN_PROGRESS,
];

const BAR_MAX_HEIGHT = 120; // px

// ─── Component ────────────────────────────────────────────────────────
export default function OrganizerAnalytics() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const orgId = user?.organizationsAdmin[0]?.organizationId;
  const org = user?.organizationsAdmin[0]?.organization;
  const isForfaitActive = isOrgaForfaitActive(org?.plan, org?.subscriptionStatus);
  const [period, setPeriod] = useState<Period>(6);
  const [exporting, setExporting] = useState(false);

  // ─── Queries ─────────────────────────────────────────────────────
  const {
    data: competitionsData,
    isLoading: compsLoading,
    isError: compsError,
    refetch: compsRefetch,
  } = useQuery({
    queryKey: queryKeys.competitions.list({ organizationId: orgId }),
    queryFn: () => fetchAllCompetitionsForOrg(orgId!),
    enabled: !!orgId && isAuthenticated && isForfaitActive,
  });

  const {
    data: allPayments = [],
    isLoading: paymentsLoading,
    isError: paymentsError,
    refetch: paymentsRefetch,
  } = useQuery({
    queryKey: queryKeys.payments.list({ organizationId: orgId }),
    queryFn: () => fetchAllPaymentsForOrg(orgId!),
    enabled: !!orgId && isAuthenticated && isForfaitActive,
  });

  // Les concours désactivés par un super-admin sont exclus de toutes les
  // statistiques (compteurs, graphes, export) — point de filtrage unique.
  const allCompetitions = useMemo(
    () => (competitionsData ?? []).filter(c => c.status !== CompetitionStatus.DISABLED_BY_ADMIN),
    [competitionsData],
  );
  const isLoading = compsLoading || paymentsLoading;

  // ─── Period filter ────────────────────────────────────────────────
  const cutoff = useMemo(() => cutoffDate(period), [period]);

  const competitions = useMemo(
    () =>
      cutoff
        ? allCompetitions.filter(c => {
            if (c.date == null) return false;
            return new Date(c.date) >= cutoff;
          })
        : allCompetitions,
    [allCompetitions, cutoff],
  );

  const completedPayments = useMemo(() => allPayments.filter(p => p.status === PaymentStatus.COMPLETED), [allPayments]);

  // Payments scoped to period (via competition membership)
  const periodCompIds = useMemo(() => new Set(competitions.map(c => c.id)), [competitions]);

  const filteredPayments = useMemo(
    () => completedPayments.filter(p => periodCompIds.has(p.competitionId)),
    [completedPayments, periodCompIds],
  );

  // ─── Stat card metrics ─────────────────────────────────────────────
  const totalTeams = useMemo(() => competitions.reduce((s, c) => s + c.currentTeams, 0), [competitions]);

  const avgFillRate = useMemo(() => {
    const valid = competitions.filter(c => c.maxTeams != null && c.maxTeams > 0);
    if (valid.length === 0) return null;
    return Math.round(valid.reduce((s, c) => s + (c.currentTeams / c.maxTeams!) * 100, 0) / valid.length);
  }, [competitions]);

  const totalRevenueCents = useMemo(() => filteredPayments.reduce((s, p) => s + p.amount, 0), [filteredPayments]);

  const activeCount = useMemo(
    () => competitions.filter(c => ACTIVE_STATUSES.includes(c.status)).length,
    [competitions],
  );

  // ─── Monthly bar chart ─────────────────────────────────────────────
  // Always shows last N months (tied to period); uses allCompetitions to capture full history
  const chartMonths = useMemo(() => lastNMonths(period === 0 ? 12 : period), [period]);

  const monthlyTeams = useMemo(() => {
    const byMonth = new Map<string, number>(chartMonths.map(m => [m.key, 0]));
    for (const c of allCompetitions) {
      const key = c.date ? monthKey(c.date) : null;
      if (key && byMonth.has(key)) byMonth.set(key, (byMonth.get(key) ?? 0) + c.currentTeams);
    }
    return chartMonths.map(m => ({ label: m.label, value: byMonth.get(m.key) ?? 0 }));
  }, [allCompetitions, chartMonths]);

  const maxMonthlyValue = useMemo(() => Math.max(...monthlyTeams.map(m => m.value), 1), [monthlyTeams]);

  // ─── Revenue per competition (top 5) ──────────────────────────────
  const revenueByComp = useMemo(() => {
    const byId = new Map<number, number>();
    for (const p of filteredPayments) {
      byId.set(p.competitionId, (byId.get(p.competitionId) ?? 0) + p.amount);
    }
    return Array.from(byId.entries())
      .map(([id, amount]) => ({
        id,
        name: allCompetitions.find(c => c.id === id)?.name ?? `Concours #${id}`,
        amount,
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5);
  }, [filteredPayments, allCompetitions]);

  const maxRevenue = useMemo(() => Math.max(...revenueByComp.map(r => r.amount), 1), [revenueByComp]);

  // ─── Top competitions table ────────────────────────────────────────
  const topCompetitions = useMemo(
    () =>
      [...competitions]
        .sort((a, b) => b.currentTeams - a.currentTeams)
        .slice(0, 5)
        .map(c => {
          const revenue = filteredPayments.filter(p => p.competitionId === c.id).reduce((s, p) => s + p.amount, 0);
          const fillRate = c.maxTeams && c.maxTeams > 0 ? Math.round((c.currentTeams / c.maxTeams) * 100) : null;
          return { ...c, revenue, fillRate };
        }),
    [competitions, filteredPayments],
  );

  // ─── Format distribution ──────────────────────────────────────────
  const typeDistribution = useMemo(() => {
    const byType = new Map<CompetitionType, number>();
    for (const c of competitions) {
      if (c.type == null) continue;
      byType.set(c.type, (byType.get(c.type) ?? 0) + c.currentTeams);
    }
    const total = Array.from(byType.values()).reduce((s, v) => s + v, 0);
    return Object.values(CompetitionType)
      .map(type => ({
        type,
        label: TYPE_LABELS[type],
        count: byType.get(type) ?? 0,
        percent: total > 0 ? Math.round(((byType.get(type) ?? 0) / total) * 100) : 0,
      }))
      .filter(d => d.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [competitions]);

  // ─── Insights ─────────────────────────────────────────────────────
  const bestFillComp = useMemo(() => {
    const valid = competitions.filter(c => c.maxTeams != null && c.maxTeams > 0);
    if (valid.length === 0) return null;
    return valid.reduce((best, c) => (c.currentTeams / c.maxTeams! > best.currentTeams / best.maxTeams! ? c : best));
  }, [competitions]);

  const topType = typeDistribution[0];

  // ─── Export ─────────────────────────────────────────────────────────
  interface CompExportRow extends Competition {
    revenue: number;
    fillRate: number | null;
  }

  const exportColumns: ExportColumn<CompExportRow>[] = [
    { header: 'Nom', value: 'name' },
    { header: 'Date', value: c => (c.date ? formatCompetitionDate(c.date) : '—') },
    { header: 'Format', value: c => (c.type ? (TYPE_LABELS[c.type] ?? c.type) : '—') },
    { header: 'Statut', value: c => c.status },
    { header: 'Équipes', value: 'currentTeams' },
    { header: 'Capacité', value: c => c.maxTeams ?? '' },
    { header: 'Remplissage (%)', value: c => c.fillRate ?? '', numFmt: '#,##0' },
    { header: 'Revenus (€)', value: c => c.revenue / 100, numFmt: '#,##0.00' },
  ];

  async function handleExport(format: ExportFormat = 'csv') {
    if (competitions.length === 0) return;
    setExporting(true);
    try {
      const rows: CompExportRow[] = competitions.map(c => {
        const revenue = filteredPayments.filter(p => p.competitionId === c.id).reduce((s, p) => s + p.amount, 0);
        const fillRate = c.maxTeams && c.maxTeams > 0 ? Math.round((c.currentTeams / c.maxTeams) * 100) : null;
        return { ...c, revenue, fillRate };
      });
      await exportData(rows, exportColumns, datedFilename('analytics-concours', format), format, {
        sheetName: 'Analytics',
      });
    } finally {
      setExporting(false);
    }
  }

  // ─── Error state ───────────────────────────────────────────────────
  if (compsError || paymentsError) {
    return (
      <DashboardLayout sidebarConfig={organizerSidebar}>
        <div className='p-8'>
          <PageHeader title='Analytics' subtitle='Statistiques détaillées de votre activité' />
          <QueryErrorState
            onRetry={() => {
              if (compsError) void compsRefetch();
              if (paymentsError) void paymentsRefetch();
            }}
          />
        </div>
      </DashboardLayout>
    );
  }

  // ─── Empty state ───────────────────────────────────────────────────
  if (!isLoading && allCompetitions.length === 0) {
    return (
      <DashboardLayout sidebarConfig={organizerSidebar}>
        <div className='p-8'>
          <PageHeader title='Analytics' subtitle='Statistiques détaillées de votre activité' />
          <div className='flex flex-col items-center justify-center py-24 text-slate-400 gap-4'>
            <BarChart3 className='w-16 h-16 opacity-20' />
            <p className='text-base font-medium'>Aucun concours organisé pour le moment</p>
            <p className='text-sm'>Les statistiques apparaîtront dès votre premier concours</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={organizerSidebar}>
      <PremiumGate
        isAllowed={isForfaitActive}
        title='Analytics (Forfait)'
        description='Passez au Forfait pour débloquer les statistiques détaillées de votre activité.'
        ctaLabel='Voir les offres'
        ctaTo='/organizer/settings?tab=subscription'
      >
        <div className='p-8'>
          <PageHeader
            title='Analytics'
            subtitle='Statistiques détaillées de votre activité'
            actions={
              <div className='flex items-center gap-3'>
                <div className='flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1'>
                  {([3, 6, 12, 0] as Period[]).map(p => (
                    <button
                      key={p}
                      onClick={() => setPeriod(p)}
                      className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
                        period === p ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {PERIOD_LABELS[p]}
                    </button>
                  ))}
                </div>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => handleExport('csv')}
                    disabled={exporting || isLoading || competitions.length === 0}
                    aria-label='Exporter les statistiques en CSV'
                    className='flex items-center gap-2 px-5 py-2 bg-white border border-slate-200 rounded-xl hover:border-blue-300 transition disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <Download className='w-4 h-4 text-slate-600' />
                    <span className='text-sm font-semibold text-slate-700'>{exporting ? 'Export…' : 'CSV'}</span>
                  </button>
                  <button
                    onClick={() => handleExport('xlsx')}
                    disabled={exporting || isLoading || competitions.length === 0}
                    aria-label='Exporter les statistiques en Excel'
                    className='flex items-center gap-2 px-5 py-2 bg-white border border-slate-200 rounded-xl hover:border-blue-300 transition disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <Download className='w-4 h-4 text-slate-600' />
                    <span className='text-sm font-semibold text-slate-700'>{exporting ? 'Export…' : 'Excel'}</span>
                  </button>
                </div>
              </div>
            }
          />

          {/* ─── Stat Cards ── */}
          {isLoading ? (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className='h-28 animate-pulse rounded-2xl bg-slate-100' />
              ))}
            </div>
          ) : (
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
              <StatCard
                icon={Users2}
                iconBg='bg-blue-100'
                iconColor='text-blue-600'
                label='Équipes inscrites'
                value={String(totalTeams)}
                subtitle={period === 0 ? 'Toutes périodes' : `Sur ${PERIOD_LABELS[period]}`}
              />
              <StatCard
                icon={Percent}
                iconBg='bg-emerald-100'
                iconColor='text-emerald-600'
                label='Taux de remplissage moyen'
                value={avgFillRate !== null ? `${avgFillRate}%` : '—'}
                subtitle='Concours avec capacité définie'
              />
              <StatCard
                icon={Euro}
                iconBg='bg-purple-100'
                iconColor='text-purple-600'
                label='Revenus encaissés'
                value={formatEur(totalRevenueCents, 0)}
                subtitle='Paiements confirmés'
              />
              <StatCard
                icon={Trophy}
                iconBg='bg-amber-100'
                iconColor='text-amber-600'
                label='Concours actifs'
                value={String(activeCount)}
                subtitle='Inscriptions ouvertes ou en cours'
              />
            </div>
          )}

          {/* ─── Charts Row ── */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8'>
            {/* Monthly inscriptions */}
            <div className='bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white'>
              <div className='flex items-center justify-between mb-6'>
                <div>
                  <h2 className='text-lg font-bold flex items-center gap-2'>
                    <BarChart3 className='w-5 h-5' />
                    Équipes par mois
                  </h2>
                  <p className='text-blue-200 text-sm mt-1'>Selon la date des concours</p>
                </div>
              </div>

              {isLoading ? (
                <div className='h-40 animate-pulse bg-white/10 rounded-xl' />
              ) : (
                <>
                  <div className='flex items-end justify-between gap-2' style={{ height: `${BAR_MAX_HEIGHT}px` }}>
                    {monthlyTeams.map(bar => {
                      const barH =
                        maxMonthlyValue > 0
                          ? Math.max(Math.round((bar.value / maxMonthlyValue) * BAR_MAX_HEIGHT), bar.value > 0 ? 6 : 0)
                          : 0;
                      return (
                        <div key={bar.label} className='flex-1 flex flex-col items-center justify-end gap-1'>
                          {bar.value > 0 && (
                            <span className='text-[10px] text-blue-200 font-semibold leading-none'>{bar.value}</span>
                          )}
                          <div
                            className='w-full bg-white/40 rounded-t'
                            style={{ height: `${barH}px`, opacity: bar.value === 0 ? 0.2 : 1 }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className='flex justify-between gap-2 mt-2'>
                    {monthlyTeams.map(bar => (
                      <div key={bar.label} className='flex-1 text-center'>
                        <span className='text-[10px] text-blue-200'>{bar.label}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Revenue per competition */}
            <div className='bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-8 text-white'>
              <div className='flex items-center justify-between mb-6'>
                <div>
                  <h2 className='text-lg font-bold flex items-center gap-2'>
                    <Euro className='w-5 h-5' />
                    Revenus par concours
                  </h2>
                  <p className='text-emerald-200 text-sm mt-1'>Top 5 des concours payants</p>
                </div>
              </div>

              {isLoading ? (
                <div className='h-40 animate-pulse bg-white/10 rounded-xl' />
              ) : revenueByComp.length === 0 ? (
                <div className='flex flex-col items-center justify-center h-36 gap-3 text-emerald-200'>
                  <Euro className='w-10 h-10 opacity-30' />
                  <p className='text-sm text-center'>Aucun paiement encaissé sur la période</p>
                </div>
              ) : (
                <div className='space-y-4'>
                  {revenueByComp.map(item => (
                    <div key={item.id}>
                      <div className='flex items-center justify-between mb-1'>
                        <span className='text-sm text-emerald-100 truncate max-w-[60%]'>{item.name}</span>
                        <span className='text-sm font-bold shrink-0'>{formatEur(item.amount, 0)}</span>
                      </div>
                      <div className='h-2 bg-white/20 rounded-full overflow-hidden'>
                        <div
                          className='h-full bg-white/60 rounded-full'
                          style={{ width: `${Math.round((item.amount / maxRevenue) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ─── Bottom Grid ── */}
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
            {/* Top competitions table */}
            <div className='lg:col-span-2'>
              <div className='bg-white rounded-2xl border border-slate-200'>
                <div className='px-6 py-4 border-b border-slate-200'>
                  <h2 className='text-lg font-bold text-slate-900'>Top concours</h2>
                </div>

                <div className='grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider'>
                  <div className='col-span-1'>#</div>
                  <div className='col-span-4'>Nom</div>
                  <div className='col-span-2'>Équipes</div>
                  <div className='col-span-2'>Revenus</div>
                  <div className='col-span-3'>Remplissage</div>
                </div>

                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className='grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-50'>
                      <div className='col-span-1 h-4 animate-pulse rounded bg-slate-200' />
                      <div className='col-span-4 h-4 animate-pulse rounded bg-slate-200' />
                      <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                      <div className='col-span-2 h-4 animate-pulse rounded bg-slate-200' />
                      <div className='col-span-3 h-4 animate-pulse rounded bg-slate-200' />
                    </div>
                  ))
                ) : topCompetitions.length === 0 ? (
                  <div className='py-12 text-center text-sm text-slate-400'>
                    Aucun concours sur la période sélectionnée
                  </div>
                ) : (
                  topCompetitions.map((comp, index) => (
                    <div
                      key={comp.id}
                      className='grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-50 items-center hover:bg-slate-50 transition'
                    >
                      <div className='col-span-1'>
                        <span className='text-sm font-bold text-slate-400'>{index + 1}</span>
                      </div>
                      <div className='col-span-4 min-w-0'>
                        <p className='font-semibold text-slate-900 text-sm truncate'>{comp.name}</p>
                        <p className='text-xs text-slate-400'>{formatCompetitionDate(comp.date)}</p>
                      </div>
                      <div className='col-span-2'>
                        <span className='text-sm font-semibold text-slate-700'>{comp.currentTeams}</span>
                        {comp.maxTeams != null && <span className='text-xs text-slate-400'>/{comp.maxTeams}</span>}
                      </div>
                      <div className='col-span-2'>
                        {comp.revenue > 0 ? (
                          <span className='text-sm font-bold text-slate-900'>{formatEur(comp.revenue, 0)}</span>
                        ) : (
                          <span className='text-xs text-slate-300'>Gratuit</span>
                        )}
                      </div>
                      <div className='col-span-3'>
                        {comp.fillRate !== null ? (
                          <div className='flex items-center gap-2'>
                            <div className='flex-1 bg-slate-200 rounded-full h-2'>
                              <div
                                className={`h-2 rounded-full ${
                                  comp.fillRate === 100
                                    ? 'bg-emerald-500'
                                    : comp.fillRate >= 70
                                      ? 'bg-blue-500'
                                      : 'bg-amber-500'
                                }`}
                                style={{ width: `${comp.fillRate}%` }}
                              />
                            </div>
                            <span className='text-xs font-semibold text-slate-600 w-9 text-right shrink-0'>
                              {comp.fillRate}%
                            </span>
                          </div>
                        ) : (
                          <span className='text-xs text-slate-300'>—</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Right sidebar */}
            <div className='space-y-6'>
              {/* Format distribution */}
              <div className='bg-white rounded-2xl border border-slate-200 p-6'>
                <h3 className='font-bold text-slate-900 mb-4 flex items-center gap-2'>
                  <Users2 className='w-5 h-5 text-blue-600' />
                  Formats de jeu
                </h3>

                {isLoading ? (
                  <div className='space-y-4'>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className='h-8 animate-pulse rounded bg-slate-100' />
                    ))}
                  </div>
                ) : typeDistribution.length === 0 ? (
                  <p className='text-sm text-slate-400'>Aucune donnée disponible</p>
                ) : (
                  <>
                    <div className='space-y-4'>
                      {typeDistribution.map(d => (
                        <div key={d.type}>
                          <div className='flex items-center justify-between mb-1'>
                            <span className='text-sm text-slate-700'>{d.label}</span>
                            <span className='text-xs font-semibold text-slate-500'>
                              {d.count} équipe{d.count !== 1 ? 's' : ''} ({d.percent}%)
                            </span>
                          </div>
                          <div className='h-2 bg-slate-100 rounded-full overflow-hidden'>
                            <div
                              className={`h-full rounded-full ${TYPE_COLORS[d.type]}`}
                              style={{ width: `${d.percent}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    {totalTeams > 0 && (
                      <div className='mt-4 pt-4 border-t border-slate-100'>
                        <p className='text-sm text-slate-500'>
                          Total : <span className='font-bold text-slate-900'>{totalTeams.toLocaleString('fr-FR')}</span>{' '}
                          équipes
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Insights */}
              <div className='bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-6 text-white'>
                <h3 className='font-bold mb-4 flex items-center gap-2'>
                  <TrendingUp className='w-5 h-5' />
                  Insights
                </h3>

                {isLoading ? (
                  <div className='space-y-3'>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className='h-14 animate-pulse rounded-xl bg-white/10' />
                    ))}
                  </div>
                ) : (
                  <div className='space-y-3'>
                    {bestFillComp && (
                      <div className='bg-white/10 backdrop-blur rounded-xl p-3'>
                        <p className='text-xs text-purple-200 mb-1'>Meilleur remplissage</p>
                        <p className='font-semibold text-sm truncate'>{bestFillComp.name}</p>
                        {bestFillComp.maxTeams && (
                          <p className='text-xs text-purple-200 mt-0.5'>
                            {bestFillComp.currentTeams}/{bestFillComp.maxTeams} équipes
                          </p>
                        )}
                      </div>
                    )}
                    {topType && (
                      <div className='bg-white/10 backdrop-blur rounded-xl p-3'>
                        <p className='text-xs text-purple-200 mb-1'>Format le plus populaire</p>
                        <p className='font-semibold text-sm'>{topType.label}</p>
                        <p className='text-xs text-purple-200 mt-0.5'>{topType.percent}% des équipes</p>
                      </div>
                    )}
                    <div className='bg-white/10 backdrop-blur rounded-xl p-3'>
                      <p className='text-xs text-purple-200 mb-1'>Concours organisés</p>
                      <p className='font-semibold text-sm'>{allCompetitions.length} au total</p>
                      {competitions.length !== allCompetitions.length && (
                        <p className='text-xs text-purple-200 mt-0.5'>{competitions.length} sur la période</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </PremiumGate>
    </DashboardLayout>
  );
}
